import type { ProfileStore } from '../sim/meta/ProfileStore';

/**
 * The Electron preload exposes this bridge (contextBridge → `descentBridge`):
 * synchronous file-backed profile persistence in the app's userData dir, so
 * progression survives file:// localStorage partitioning and SD-card moves.
 * Absent in the browser — main.ts falls back to LocalStorageProfileStore.
 * `journalAppend` is OPTIONAL: older installed shells predate it and the
 * renderer must keep working against them.
 */
export interface DescentBridge {
  profileLoad(): string | null;
  profileSave(raw: string): void;
  journalAppend?(text: string): void;
}

declare global {
  interface Window {
    descentBridge?: DescentBridge;
  }
}

/**
 * Writes are DEBOUNCED (2026-07-27, after a whole-Deck freeze while mashing
 * artifact upgrades): Profile.save() fires on every mutation, so mashing
 * produced a synchronous SD-card write per press. Rapid saves now coalesce
 * to one disk write per window; the trailing write flushes when the page
 * hides or the window closes, so a normal quit never loses it. A hard
 * crash risks ≤ the debounce window of progress — the store's existing
 * contract ("a missed save loses seconds, not the profile") accepts worse.
 */
const SAVE_DEBOUNCE_MS = 250;

export class FileProfileStore implements ProfileStore {
  private pending: string | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly bridge: DescentBridge) {
    // Guarded for headless tests (vitest runs in node — no window).
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', () => this.flush());
      window.addEventListener('beforeunload', () => this.flush());
      document.addEventListener('visibilitychange', () => {
        // Hidden windows throttle timers — flush instead of trusting one.
        if (document.visibilityState === 'hidden') this.flush();
      });
    }
  }

  load(): string | null {
    try {
      return this.bridge.profileLoad();
    } catch {
      return null;
    }
  }

  save(raw: string): void {
    this.pending = raw;
    if (this.timer !== null) return; // a write is already scheduled
    this.timer = setTimeout(() => {
      this.timer = null;
      this.flush();
    }, SAVE_DEBOUNCE_MS);
  }

  /** Write the newest pending state NOW (idempotent). */
  flush(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.pending === null) return;
    const raw = this.pending;
    this.pending = null;
    try {
      this.bridge.profileSave(raw);
    } catch { /* a missed save loses seconds of progress, not the profile */ }
  }
}
